export type dateStringOrDate = Date | string | null | undefined;

export const getDate = (date: dateStringOrDate): Date | undefined => {
  if (!date) return undefined;
  if (typeof date === 'string') {
    // 2023-11-25T01:34:38.03Z
    // 2018-12-30T20:59:00.000Z
    date = new Date(date);
  }
  if (!(date instanceof Date) || isNaN(date.getTime())) {
    return undefined;
  }
  return date;
};

export const dayDiff = (to: dateStringOrDate, from: dateStringOrDate): number => {
  const fromDate = getDate(from);
  const toDate = getDate(to);

  if (!fromDate || !toDate) return 0;

  const msPerDay = 1000 * 60 * 60 * 24;

  const diffInMs = toDate.getTime() - fromDate.getTime();
  return Math.floor(diffInMs / msPerDay);
};

export const minutesDiff = (from: dateStringOrDate, to: dateStringOrDate): number => {
  const fromDate = getDate(from);
  const toDate = getDate(to);

  if (!fromDate || !toDate) return 0;

  const msPerMinute = 1000 * 60;

  const diffInMs = toDate.getTime() - fromDate.getTime();
  return Math.floor(diffInMs / msPerMinute);
};

export type DateFormatOptions = {
  withTime?: boolean;
  language?: string;
};

export const formatDate = (date: dateStringOrDate, { withTime, language }: DateFormatOptions = {}) => {
  const d = getDate(date);
  if (!d) return undefined;

  return new Intl.DateTimeFormat(language || undefined, {
    dateStyle: 'medium',
    timeStyle: withTime ? 'short' : undefined,
  }).format(d);
};

export type DurationFormatOptions = {
  language?: string;
  unitDisplay?: 'long' | 'short' | 'narrow';
};

export const formatDuration = (durationInHours: number, { language, unitDisplay = 'short' }: DurationFormatOptions = {}): string => {
  if (durationInHours < 0) {
    durationInHours = 0;
  }

  const hours = Math.floor(durationInHours);
  const minutes = Math.round((durationInHours - hours) * 60);

  const formatPart = (value: number, unit: 'hour' | 'minute') => {
    return new Intl.NumberFormat(language || undefined, {
      style: 'unit',
      unit,
      unitDisplay,
    }).format(value);
  };

  if (hours > 0 && minutes > 0) {
    return `${formatPart(hours, 'hour')} and ${formatPart(minutes, 'minute')}`;
  } else if (hours > 0) {
    return formatPart(hours, 'hour');
  } else {
    return formatPart(minutes, 'minute');
  }
};
